import { FileText, Library } from "lucide-react";
import { useMemo, type FC } from "react";

import { toolPartsOf } from "@/features/caseIde/chat/components/detailsSplit";
import { toolNameOf } from "@/features/caseIde/chat/components/ToolCard";
import { outputOfPart } from "@/features/caseIde/chat/components/ToolRow";
import type { CaseAgentUIMessage } from "@/features/caseIde/chat/useCaseAgentChat";

import { chatSourcesOf, isLibraryTool, type LibraryCall } from "../chatSources";
import { WeightBadge } from "./WeightBadge";

/** The library calls a reply made, with what each returned. */
export const libraryCallsOf = (message: CaseAgentUIMessage): LibraryCall[] =>
  toolPartsOf(message.parts)
    .map((part) => ({ tool: toolNameOf(part), output: outputOfPart(part) }))
    .filter((call) => isLibraryTool(call.tool) && call.output !== undefined);

const answerOf = (message: CaseAgentUIMessage): string =>
  message.parts.map((p) => (p.type === "text" ? p.text : "")).join("\n");

type Props = {
  message: CaseAgentUIMessage;
  onOpen: (id: string) => void;
};

/**
 * Under an "Ask the Library" answer: the library documents it rests on, each
 * opening in the Research reader. A reply that called no library tool says
 * so, since the answer then did not come from the library.
 */
export const LibrarySources: FC<Props> = ({ message, onOpen }) => {
  const calls = useMemo(() => libraryCallsOf(message), [message]);
  const sources = useMemo(
    () => chatSourcesOf(calls, answerOf(message)),
    [calls, message]
  );
  if (!calls.length) {
    return (
      <p
        className="flex items-center gap-1.5 text-sm text-fg-3"
        data-testid="library-sources-none"
      >
        <Library className="size-3.5 shrink-0" />
        This answer did not search the library.
      </p>
    );
  }
  if (!sources.length) {
    return (
      <p
        className="flex items-center gap-1.5 text-sm text-fg-3"
        data-testid="library-sources-empty"
      >
        <Library className="size-3.5 shrink-0" />
        The library returned no documents for this question.
      </p>
    );
  }
  const cited = sources.some((s) => s.cited);
  return (
    <section
      className="mt-1 flex flex-col gap-1"
      aria-label={cited ? "Sources" : "Library Results"}
      data-testid="library-sources"
    >
      <span className="text-sm font-medium text-fg-3">
        {cited ? "Sources" : "Library Results"}
      </span>
      <ul className="flex flex-col overflow-hidden rounded-md ring-1 ring-line-2">
        {sources.map((s) => (
          <li key={s.id} className="not-first:border-t not-first:border-line-2">
            <button
              type="button"
              onClick={() => onOpen(s.id)}
              className="flex w-full min-w-0 items-center gap-2 px-2.5 py-1.5 text-left hover:bg-fill-1 focus-visible:bg-fill-1 focus-visible:outline-none"
              aria-label={`Open ${s.title}`}
              data-testid="library-source"
            >
              <FileText className="size-3.5 shrink-0 text-fg-3" />
              <span className="min-w-0 flex-1 truncate text-base text-fg">
                {s.title}
                {s.citation && !s.title.includes(s.citation) ? (
                  <span className="text-fg-3">, {s.citation}</span>
                ) : null}
              </span>
              <WeightBadge weight={s.weight} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};
